import React from "react"
import { Paper, makeStyles } from "@material-ui/core"
import type { ReportedHourViewModel } from "../../hour-reports/api"
import { convertDecimalHoursToHoursAndMinutes } from "../../hour-reports/utils"
import { makeOnSearchChange } from "../base/event-handlers/on-search-change"
import { useTranslations } from "../hooks/use-translations"
import { useTreeGridFilters } from "../hooks/use-treegrid-filters"
import { createTreegrid, destroyTreegrid, updateGridData } from "../utils/tree-grid"
import {
  approvedHoursDefaultColumnOrder,
  approvedHoursDefaultVisibleColumns,
  getApprovedHoursColumnsForContext,
} from "./column-settings"
import { makeLayout } from "./layout"
import { makeTreeGridApprovedHourRows } from "./rows"
import { ApprovedHoursVisibleColumnName } from "./types"
import type {
  TreeGridApprovedHourDateGroup,
  TreeGridApprovedHourDateGroupRow,
  TreeGridApprovedHourRow,
} from "./rows/types"
import { HourReportDayAuditViewModel } from "../../hour-reports/api/hour-report-day-audit"
import { handleChangeApproved } from "./handlers/on-change-approved"
import { handleChangeReadyForCheck } from "./handlers/on-change-ready-for-check"
import type { TotalReadyForReviewRequest } from "./handlers/on-change-ready-for-check"
import { handleChangeReadyForApproval } from "./handlers/on-change-ready-for-approval"
import type { TotalReadyForApprovalRequest } from "./handlers/on-change-ready-for-approval"
import { useHourReportMutations } from "../../hour-reports/hooks/use-hour-report-mutations"
import parse from "date-fns/parse"
import { TOTAL_APPROVED_HOURS_ROW_ID } from "./rows/total-hours-row"
import { useRefreshTotalHoursRow } from "./use-refresh-total-hours-row"
import RequestReadyForCheckDialog, { RequestReadyForCheckReportSummary } from "./components/RequestReadyForCheckDialog"
import RequestReadyForApprovalDialog from "./components/RequestReadyForApprovalDialog"
import TotalReadyForApprovalDialog from "./components/TotalReadyForApprovalDialog"
import TotalReadyForReviewDialog from "./components/TotalReadyForReviewDialog"
import type { HourReportingWorkflowSettings } from "../../hour-reports/hooks/use-hour-reporting-workflow-settings"
import { useHourReportTranslations } from "../hooks/use-hour-report-translations"

const id = "__treegrid_approved_hours__"

const ApprovedHoursTable = (props: ApprovedHoursTableProps) => {
  const classes = useStyles()
  const translations = useTranslations()
  const { noDataText, labels, notSelected, actions, audit } = useHourReportTranslations()
  const rowTranslations = React.useMemo(
    () => ({
      notSelected,
      add: actions.add,
      edit: actions.edit,
      delete: actions.delete,
      reported: audit.reported,
      readyForCheck: audit.readyForCheck,
      checked: audit.checked,
      approved: audit.approved,
      statusTip: audit.by,
    }),
    [actions, audit, notSelected]
  )
  const {
    setDaysReadyForCheck,
    setDaysReadyForApproval,
    setDaysApproved,
    updateHourReportInfo,
    checkHourReport,
    approveHourReport,
    performHourReportAdminAction,
    performHourReportDayAuditAdminAction,
    requestReadyForCheck,
    requestReadyForApproval,
    meta,
  } = useHourReportMutations({ supportsChecking: props.workflowSettings?.supportsChecking })

  const {
    reports,
    dayAudits,
    selectedMembershipId,
    enableAddingReports,
    dateFormat,
    dateSeparator,
    firstDayOfWeek,
    gridInfo,
    fromDate,
    toDate,
    onAddHourReport,
    onEditHourReport,
    onDeleteHourReport,
    workflowSettings,
    isOrganisationContext = false,
  } = props
  const columnOrder = React.useMemo(
    () =>
      getApprovedHoursColumnsForContext(props.columnOrder ?? approvedHoursDefaultColumnOrder, isOrganisationContext),
    [isOrganisationContext, props.columnOrder]
  )
  const visibleColumns = React.useMemo(
    () =>
      getApprovedHoursColumnsForContext(
        props.visibleColumns ?? getDefaultVisibleColumns({ reports }),
        isOrganisationContext
      ),
    [isOrganisationContext, props.visibleColumns, reports]
  )

  const isMemberSubContext = Boolean(selectedMembershipId)
  const [readyForCheckRequest, setReadyForCheckRequest] = React.useState<RequestReadyForCheckReportSummary | null>(null)
  const [readyForApprovalRequest, setReadyForApprovalRequest] =
    React.useState<RequestReadyForCheckReportSummary | null>(null)
  const [totalReadyForReviewRequest, setTotalReadyForReviewRequest] = React.useState<TotalReadyForReviewRequest | null>(
    null
  )
  const [totalReadyForApprovalRequest, setTotalReadyForApprovalRequest] =
    React.useState<TotalReadyForApprovalRequest | null>(null)
  const { showFilters, toggleFilters } = useTreeGridFilters({ gridId: id })
  const data = React.useMemo(
    () =>
      makeTreeGridApprovedHourRows({
        reports,
        dayAudits,
        dateFormat,
        fromDate,
        toDate,
        selectedMemberForDayAudit: selectedMembershipId,
        enableAddingReports,
        translations: rowTranslations,
      }),
    [dateFormat, fromDate, reports, dayAudits, toDate, selectedMembershipId, enableAddingReports, rowTranslations]
  )
  const layout = React.useMemo(
    () =>
      makeLayout({
        id,
        cfgId: `${id}-${isOrganisationContext ? "organisation" : "personal"}`,
        dateFormat,
        dateSeparator,
        firstDayOfWeek,
        gridInfo,
        noDataText,
        showFilters,
        translations,
        columnOrder,
        visibleColumns,
        isMemberSubContext,
        workflowSettings,
        labels,
      }),
    [
      columnOrder,
      dateFormat,
      dateSeparator,
      firstDayOfWeek,
      gridInfo,
      noDataText,
      visibleColumns,
      showFilters,
      translations,
      isMemberSubContext,
      isOrganisationContext,
      labels,
      workflowSettings,
    ]
  )
  const onSearchChange = React.useMemo(() => makeOnSearchChange({ translations }), [translations])
  const { refreshTotalHoursRow } = useRefreshTotalHoursRow(id, data, isMemberSubContext)

  const handleCheckTotalReadyForReview = async (notifyChecker: boolean) => {
    if (!selectedMembershipId || !totalReadyForReviewRequest) return false

    const didUpdate = await setDaysReadyForCheck({
      membershipId: selectedMembershipId,
      days: totalReadyForReviewRequest.days,
      status: "submitted",
      notifyChecker,
    })

    if (didUpdate) {
      setTotalReadyForReviewRequest(null)
      refreshTotalHoursRow()
    }

    return didUpdate
  }

  const handleCheckTotalReadyForApproval = async (notifyApprover: boolean) => {
    if (!selectedMembershipId || !totalReadyForApprovalRequest) return false

    const didUpdate = await setDaysReadyForApproval({
      membershipId: selectedMembershipId,
      days: totalReadyForApprovalRequest.days,
      status: "checked",
      notifyApprover,
    })

    if (didUpdate) {
      setTotalReadyForApprovalRequest(null)
      refreshTotalHoursRow()
    }

    return didUpdate
  }

  React.useEffect(() => {
    createTreegrid({ id, data, layout })
    return () => destroyTreegrid(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrganisationContext])

  React.useEffect(() => {
    updateGridData({ id, data: keepExpandedRows(data) })
  }, [data])

  // @ts-ignore
  window.Grids.OnFilterToggle = function () {
    toggleFilters()
  }

  window.Grids.OnRenderFinish = function (grid: TGrid) {
    if (!grid) return

    refreshTotalHoursRow()
  }

  window.Grids.OnFilterFinish = function (grid: TGrid) {
    if (!grid) return

    const totalHoursRow = grid.GetRowById(TOTAL_APPROVED_HOURS_ROW_ID)
    if (!totalHoursRow) return

    grid.SetValue(totalHoursRow, "hours", "")
    grid.RefreshCell(totalHoursRow, "hours")
  }

  // @ts-expect-error TreeGrid event hooks are stored on the global Grids object.
  window.Grids.OnSearchChange = function (grid: TGrid, searchQuery: string) {
    onSearchChange(grid, searchQuery)
  }

  window.Grids.OnValueChanged = function (
    grid: TGrid,
    row: TreeGridApprovedHourDateGroupRow | TreeGridApprovedHourRow,
    column: string,
    newValue: unknown,
    oldValue: unknown
  ) {
    if (newValue === oldValue) return
    const membershipId = selectedMembershipId

    if (column === "readyForCheck") {
      return handleChangeReadyForCheck({
        grid,
        row,
        checked: Boolean(newValue),
        updateHourReportStatus: ({ id, status }) => updateHourReportInfo(id, { status }),
        performHourReportAdminAction: ({ id, action }) => performHourReportAdminAction(id, { action }),
        performDayAuditAdminAction: (updates) => {
          if (membershipId) return performHourReportDayAuditAdminAction({ ...updates, membershipId })
        },
        setDaysReadyForCheck: (updates) => {
          if (membershipId) return setDaysReadyForCheck({ ...updates, membershipId })
        },
        confirmAllReportsReadyForReview: setTotalReadyForReviewRequest,
      })
    }

    if (column === "readyForApproval") {
      return handleChangeReadyForApproval({
        grid,
        row,
        checked: Boolean(newValue),
        oldValue,
        requestReadyForCheck: setReadyForCheckRequest,
        checkHourReport: ({ id, status }) => checkHourReport(id, { status }),
        performHourReportAdminAction: ({ id, action }) => performHourReportAdminAction(id, { action }),
        performDayAuditAdminAction: (updates) => {
          if (membershipId) return performHourReportDayAuditAdminAction({ ...updates, membershipId })
        },
        setDaysReadyForApproval: (updates) => {
          if (membershipId) return setDaysReadyForApproval({ ...updates, membershipId })
        },
        confirmAllReportsReadyForApproval: setTotalReadyForApprovalRequest,
      })
    }

    if (column === "approved") {
      return handleChangeApproved({
        grid,
        row,
        checked: Boolean(newValue),
        requestReadyForCheck: setReadyForCheckRequest,
        requestReadyForApproval: setReadyForApprovalRequest,
        approveHourReport: ({ id, status }) => approveHourReport(id, { status }),
        performHourReportAdminAction: ({ id, action }) => performHourReportAdminAction(id, { action }),
        performDayAuditAdminAction: (updates) => {
          if (membershipId) return performHourReportDayAuditAdminAction({ ...updates, membershipId })
        },
        setDaysApproved: (updates) => {
          if (membershipId) return setDaysApproved({ ...updates, membershipId })
        },
      })
    }

    return newValue
  }

  window.Grids.OnClick = function (
    _grid: TGrid,
    row: TreeGridApprovedHourDateGroupRow,
    column: string,
    x: number,
    y: number,
    event: Event
  ) {
    if (column !== "actions") return

    const target = event.target as HTMLElement | null
    const actionButton = target?.closest("[data-approved-hours-action]")
    const action = actionButton?.getAttribute("data-approved-hours-action")

    if (action === "add" && onAddHourReport && row.date) {
      const parsedDate = parse(row.date as string, dateFormat, new Date())
      onAddHourReport(parsedDate)
      return true
    }

    if (action === "edit" && onEditHourReport) {
      onEditHourReport(row.id)
      return true
    }

    if (action === "delete" && onDeleteHourReport) {
      onDeleteHourReport(row.id)
      return true
    }

    return true
  }

  // @ts-expect-error - This function is used to format total approved hours in Column Formula
  window.Grids.FormatTotalApprovedHours = function (value: number) {
    const { hours, minutes } = convertDecimalHoursToHoursAndMinutes(value)
    return `${hours}h ${minutes}m`
  }

  return (
    <Paper variant="outlined" elevation={0} className={classes.paper}>
      <div id={id} className={classes.grid} />
      <TotalReadyForReviewDialog
        request={totalReadyForReviewRequest}
        onClose={() => setTotalReadyForReviewRequest(null)}
        onCheck={handleCheckTotalReadyForReview}
        requestState={meta.setDaysReadyForCheckMutationState}
        supportsChecking={workflowSettings?.supportsChecking}
      />
      <TotalReadyForApprovalDialog
        request={totalReadyForApprovalRequest}
        onClose={() => setTotalReadyForApprovalRequest(null)}
        onCheck={handleCheckTotalReadyForApproval}
        requestState={meta.setDaysReadyForApprovalMutationState}
      />
      <RequestReadyForCheckDialog
        report={readyForCheckRequest}
        onClose={() => setReadyForCheckRequest(null)}
        onRequestReadyForCheck={requestReadyForCheck}
        requestReadyForCheckState={meta.requestHourReportReadyForCheckState}
      />
      <RequestReadyForApprovalDialog
        report={readyForApprovalRequest}
        onClose={() => setReadyForApprovalRequest(null)}
        onRequestReadyForApproval={requestReadyForApproval}
        requestReadyForApprovalState={meta.requestHourReportReadyForApprovalState}
      />
    </Paper>
  )
}

const useStyles = makeStyles({
  paper: {
    position: "relative",
    width: "100%",
  },
  grid: {
    height: "calc(100vh - 340px)",
    minHeight: 360,
  },
})

type ApprovedHoursTableProps = {
  reports: ReportedHourViewModel[]
  dayAudits: HourReportDayAuditViewModel[]
  selectedMembershipId: string | undefined
  enableAddingReports?: boolean
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  gridInfo: string
  fromDate: Date
  toDate: Date
  onAddHourReport?: (date: Date) => void
  onEditHourReport?: (reportId: string) => void
  onDeleteHourReport?: (reportId: string) => void
  workflowSettings?: HourReportingWorkflowSettings
  isOrganisationContext?: boolean
  columnOrder?: ApprovedHoursVisibleColumnName[]
  visibleColumns?: ApprovedHoursVisibleColumnName[]
}

const keepExpandedRows = (rows: TreeGridApprovedHourDateGroup[]) => {
  const grid = window.Grids[id]
  if (!grid) return rows

  return rows.map((row) => {
    const gridRow = grid.GetRowById(row.id)
    if (!gridRow?.Expanded) return row

    return { ...row, Expanded: 1 as const }
  })
}

const getDefaultVisibleColumns = ({
  reports,
}: {
  reports: ReportedHourViewModel[]
}): ApprovedHoursVisibleColumnName[] => {
  if (!reports.length || !reports.every((report) => report.method === "manual"))
    return approvedHoursDefaultVisibleColumns

  return approvedHoursDefaultVisibleColumns.filter((column) => column !== "started" && column !== "finished")
}

export default ApprovedHoursTable
